<template>
    <div class="roster">
        <div v-if="!coaches.length && !groups.length" class="cl-empty">Δεν υπάρχουν παίκτες.</div>

        <!-- the coach(es): an info card, not a link; an active coach with a position is in his position below too -->
        <section v-if="coaches.length" class="cl-section">
            <section-heading :title="coaches.length > 1 ? 'Προπονητές' : 'Προπονητής'" icon="mdi-whistle-outline" />
            <div class="roster__coaches">
                <div
                    v-for="item in coaches"
                    :key="`coach-${item.id}`"
                    class="coach-card"
                    :style="{ '--team-color': color || '#009688' }"
                >
                    <span class="coach-card__photo">
                        <img :src="`${assetsUrl}/${item.image}`" :alt="item.name" @error="$event.target.src = defaultPlayerImg">
                    </span>
                    <span class="coach-card__text">
                        <span class="coach-card__role"><v-icon x-small color="#1b1b1b">mdi-whistle</v-icon>Προπονητής</span>
                        <span class="coach-card__name">{{ item.name }}<template v-if="item.surname"> {{ item.surname }}</template></span>
                        <span v-if="item.active && item.position" class="coach-card__also">
                            Και παίκτης · {{ positionLabel(item.position) }}<template v-if="item.alternativeJersey || item.jersey"> · #{{ item.alternativeJersey || item.jersey }}</template>
                        </span>
                    </span>
                </div>
            </div>
        </section>

        <section v-for="group in groups" :key="group.position" class="cl-section">
            <section-heading :title="group.title" :count="group.players.length" />
            <div class="roster__grid">
                <router-link
                    v-for="item in group.players"
                    :key="item.id"
                    :to="`/Player/${item.id}`"
                    class="roster-card"
                    :style="{ '--team-color': color || '#009688' }"
                >
                    <span class="roster-card__photo">
                        <img
                            :src="`${assetsUrl}/${item.image}`"
                            :alt="item.name"
                            @error="$event.target.src = defaultPlayerImg"
                        >
                    </span>
                    <span class="roster-card__number">{{ item.alternativeJersey || item.jersey }}</span>
                    <span v-if="item.coach" class="roster-card__coach" title="Και προπονητής"><v-icon x-small color="#1b1b1b">mdi-whistle</v-icon>Coach</span>
                    <span class="roster-card__name">{{ item.name }}<template v-if="item.surname"> {{ item.surname }}</template></span>
                </router-link>
            </div>
        </section>
    </div>
</template>

<script>
    import SectionHeading from '@/components/SectionHeading'

    // the positions of the roster, in court order
    const POSITION_TITLES = { guard: 'Guards', forward: 'Forwards', center: 'Centers' }

    export default {
        components: { SectionHeading },
        props: {
            players: {
                type: Array,
                default: null
            },
            color: {
                type: String,
                default: null
            }
        },
        data() {
            return {
                positions: ['guard', 'forward', 'center'],
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            // a coach is shown as coach whether active or not
            coaches() {
                return (this.players || []).filter(player => player.coach)
            },
            groups() {
                // only the active ones play; a coach plays too only when he has a position
                const players = (this.players || []).filter(player => player.active && (!player.coach || player.position))
                const groups = this.positions.map(position => ({
                    position,
                    title: POSITION_TITLES[position],
                    players: players.filter(player => player.position === position)
                }))
                // a player without a position is not shown in the roster
                return groups.filter(group => group.players.length)
            }
        },
        methods: {
            positionLabel(position) {
                return { guard: 'Guard', forward: 'Forward', center: 'Center' }[position] || position
            },
            getAvgHeight() {
                let sum = 0
                let count = 0
                this.players.forEach(player => {
                    if (player.active) {
                        sum += player.height
                        count += 1
                    }
                })
                return sum / count
            },
            getAvgAge() {
                let sum = 0
                let count = 0
                this.players.forEach(player => {
                    if (player.active && player.birthday) {
                        const dateParts = player.birthday.split('/')
                        const from = new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`)
                        const today = new Date()
                        sum += player.active ? today.getFullYear() - from.getFullYear() : 0
                        count += 1
                    }
                })
                return sum / count
            }
        }
    }
</script>

<style scoped>
.roster__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 14px;
}

.roster__coaches {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(380px, 100%), 1fr));
    gap: 14px;
}

.coach-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 16px 14px 14px;
    border-radius: 12px;
    background:
        radial-gradient(circle at 100% 0%, rgba(3, 229, 183, 0.16) 0%, rgba(3, 229, 183, 0) 55%),
        #232323;
    color: #fff;
    box-shadow: 0 6px 18px rgba(35, 35, 35, 0.14);
    overflow: hidden;
}

.coach-card::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--team-color);
}

.coach-card__photo {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex: 0 0 auto;
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background: var(--team-color);
    box-shadow: 0 0 0 3px #232323, 0 0 0 5px #03e5b7;
    overflow: hidden;
}

.coach-card__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

.coach-card__text {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1 1 auto;
    min-width: 0;
    font-family: Roboto, Arial, sans-serif;
}

.coach-card__role {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    align-self: flex-start;
    padding: 2px 10px;
    border-radius: 999px;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #1b1b1b;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.coach-card__name {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.6rem;
    line-height: 1.05;
}

.coach-card__also {
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.65);
}

.roster-card {
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    background: #232323;
    color: #fff !important;
    text-decoration: none;
    box-shadow: 0 6px 18px rgba(35, 35, 35, 0.12);
    overflow: hidden;
    transition: transform 0.15s, box-shadow 0.15s;
}

.roster-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 26px rgba(3, 122, 222, 0.22);
}

/* the photo on the colour of the team */
.roster-card__photo {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    height: 180px;
    background:
        linear-gradient(180deg, rgba(0, 0, 0, 0) 55%, rgba(35, 35, 35, 0.9) 100%),
        var(--team-color);
}

.roster-card__photo img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: bottom;
}

.roster-card__number {
    position: absolute;
    top: 8px;
    left: 12px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 2.6rem;
    line-height: 1;
    color: #fff;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}

.roster-card__coach {
    position: absolute;
    top: 10px;
    right: 10px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 8px;
    border-radius: 999px;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #1b1b1b;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.roster-card__name {
    padding: 10px 12px 12px;
    border-top: 3px solid #03e5b7;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.25;
}
</style>
